// src/pages/UploadMedicalCertificate.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Button, Paper, Typography, IconButton, Tooltip,
    Card, CardContent, Chip, CircularProgress
} from "@mui/material";
import { FaEye, FaUpload, FaDownload, FaArrowLeft } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import config from "../config/Config";
import { apiHeaderToken, apiHeaderTokenMultiPart } from "../helper/My_Helper";
import { getCandidateById } from "../helper/Api_Helper";

const actionButtons = [
    { icon: <FaEye />,     label: "View",     onClickKey: "onView",     color: "#1E88E5" },
    { icon: <FaDownload />,label: "Download", onClickKey: "onDownload", color: "#1E88E5" },
    { icon: <FaUpload />,  label: "Upload",   onClickKey: "onUpload",   color: "#43A047" },
];

const UploadMedicalCertificate = () => {
    const navigate = useNavigate();
    const uploadInputRef = useRef(null);

    
    const [medicalDoc, setMedicalDoc]       = useState(null);
    const [pendingUploadDoc, setPendingUploadDoc] = useState(null);
    const [loading, setLoading] = useState(true);

    const jsonData = sessionStorage.getItem("loginData");
    const dataObject = jsonData ? JSON.parse(jsonData) : null;

    /* -----------------------------------------------------------
       1. Fetch candidate + medical certificate document details
    ----------------------------------------------------------- */
    const fetchMedicalCertificate = useCallback(async () => {
        if (!dataObject?._id) return;

        try {
            setLoading(true);

            // 1a. Fetch candidate
            const candidateRes = await getCandidateById({
                _id: dataObject._id,
                scope_fields: [],
            });
            const candidate = candidateRes?.data ?? {};

            // 1b. Resolve applied job (same logic as VerifiedDocument)
            const appliedJob =
                candidate?.applied_jobs?.find(
                    (item) =>
                        item?.approval_note_data?.note_id &&
                        (item?.form_status === "Offer" || item?.form_status === "Hired")
                ) ||
                candidate?.applied_jobs?.find(
                    (item) => item?.job_id === candidate?.job_id
                );

            // 1c. Fetch onboarding documents and pick the Medical Certificate
            const docsRes = await axios.post(
                `${config.API_URL}getOnboardDocuments`,
                {
                    candidate_id: dataObject._id,
                    approval_note_id: appliedJob?.approval_note_data?.doc_id,
                },
                apiHeaderToken()
            );

            const allDocs = docsRes?.data?.data?.onboarding_docs || [];
            const medical = allDocs.find(
                (doc) =>
                    doc?.doc_category === "Medical Certificate" ||
                    doc?.doc_name?.toLowerCase().includes("medical")
            );
            setMedicalDoc(medical || null);
        } catch (error) {
            console.error("Error fetching medical certificate:", error);
            toast.error(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to load medical certificate"
            );
        } finally {
            setLoading(false);
        }
    }, [dataObject?._id]);

    useEffect(() => {
        fetchMedicalCertificate();
    }, [fetchMedicalCertificate]);

    /* -----------------------------------------------------------
       2. File upload handler (same as VerifiedDocument)
    ----------------------------------------------------------- */
    const handleFilesSelected = async (files, doc) => {
        if (!files || !files.length || !doc) return;

        const formData = new FormData();
        Array.from(files).forEach((file, idx) => {
            formData.append(`attachments[${idx}][doc_name]`, file?.name);
            formData.append(`attachments[${idx}][file_name]`, file);
        });

        formData.append("onboard_doc_id", doc._id);
        formData.append("approval_note_id", doc?.approval_note_doc_id);
        formData.append("candidate_id", dataObject?._id);
        formData.append("add_by_name", "Candidate");
        formData.append("add_by_mobile", "");
        formData.append("add_by_designation", "");
        formData.append("add_by_email", "");

        try {
            const response = await axios.post(
                `${config.API_URL}uploadOnboardingDocuments`,
                formData,
                apiHeaderTokenMultiPart()
            );

            if (response.status === 200) {
                toast.success(response?.data?.message || "Document uploaded successfully");
                fetchMedicalCertificate(); // refresh status
            } else {
                toast.error(
                    response?.data?.message ||
                    response?.data?.error?.message ||
                    "Upload failed"
                );
            }
        } catch (err) {
            console.error("Upload failed:", err);
            toast.error(
                err?.response?.data?.error?.message ||
                err?.response?.data?.message ||
                err?.message ||
                "Something went wrong"
            );
        }
    };

    /* -----------------------------------------------------------
       3. Action handlers (view / download / upload)
    ----------------------------------------------------------- */
    const handlers = {
        onView: (doc) => {
            const fileUrl = doc?.uploaded_file_data?.file_name
                ? config.IMAGE_PATH + doc.uploaded_file_data.file_name
                : config.IMAGE_PATH + doc?.send_file_data?.file_name;
            if (fileUrl) window.open(fileUrl, "_blank", "noopener,noreferrer");
        },
        onDownload: async (doc) => {
            const fileUrl = doc?.uploaded_file_data?.file_name
                ? config.IMAGE_PATH + doc.uploaded_file_data.file_name
                : config.IMAGE_PATH + doc?.send_file_data?.file_name;
            const fileName =
                doc?.uploaded_file_data?.file_name ||
                doc?.send_file_data?.file_name ||
                "medical-certificate";

            try {
                const response = await fetch(fileUrl, { mode: "cors" });
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (err) {
                console.error("Download failed:", err);
                toast.error("Download failed.");
            }
        },
        onUpload: (doc) => {
            setPendingUploadDoc(doc);
            uploadInputRef.current?.click();
        },
    };

    /* -----------------------------------------------------------
       4. UI
    ----------------------------------------------------------- */
    return (
        <>
            <div className="maincontent">
                <div className="container px-2 px-sm-3">
                    <div className="contentwrap" style={{ width: "100%" }}>
                        <div className="contentbox" style={{ width: "100%" }}>
                            <div className="contenthdr">
                                <Button
                                    variant="outlined"
                                    startIcon={<FaArrowLeft />}
                                    onClick={() => {
                                        if (window.history.length > 1) navigate(-1);
                                        else navigate("/dashboard");
                                    }}
                                >
                                    Back
                                </Button>
                            </div>

                            <Box>
                                <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
                                    {loading ? (
                                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                                            <CircularProgress />
                                        </Box>
                                    ) : !medicalDoc ? (
                                        <Typography textAlign="center" sx={{ py: 4 }}>
                                            Medical Certificate document is not configured for this candidate.
                                        </Typography>
                                    ) : (
                                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                            <CardContent>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        flexWrap: "wrap",
                                                        gap: 2,
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography variant="h6" fontWeight="bold">
                                                            {medicalDoc.doc_name || "Medical Certificate"}
                                                        </Typography>
                                                        <Chip
                                                            size="small"
                                                            sx={{ mt: 1 }}
                                                            label={
                                                                medicalDoc?.uploaded_file_data?.file_name
                                                                    ? "Uploaded"
                                                                    : medicalDoc?.status === "complete"
                                                                    ? "Verified"
                                                                    : "Pending"
                                                            }
                                                            color={
                                                                medicalDoc?.uploaded_file_data?.file_name
                                                                    ? "success"
                                                                    : medicalDoc?.status === "complete"
                                                                    ? "primary"
                                                                    : "warning"
                                                            }
                                                        />
                                                    </Box>

                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            gap: 1,
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        {actionButtons.map((btn, index) => (
                                                            <Tooltip key={`${btn.label}-${index}`} title={btn.label}>
                                                                <IconButton
                                                                    onClick={() => handlers[btn.onClickKey](medicalDoc)}
                                                                    sx={{
                                                                        backgroundColor: `${btn.color}10`,
                                                                        color: btn.color,
                                                                        border: `1px solid ${btn.color}30`,
                                                                    }}
                                                                >
                                                                    {btn.icon}
                                                                </IconButton>
                                                            </Tooltip>
                                                        ))}
                                                    </Box>
                                                </Box>

                                                {medicalDoc?.uploaded_file_data?.file_name && (
                                                    <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                                                        Uploaded file: {medicalDoc.uploaded_file_data.file_name}
                                                    </Typography>
                                                )}
                                            </CardContent>
                                        </Card>
                                    )}
                                </Paper>
                            </Box>
                        </div>
                    </div>
                </div>
            </div>

            <ToastContainer position="top-right" autoClose={5000} theme="dark" limit={1} />

            {/* Hidden file input */}
            <input
                type="file"
                ref={uploadInputRef}
                style={{ display: "none" }}
                onChange={(e) => {
                    handleFilesSelected(e.target.files, pendingUploadDoc);
                    e.target.value = null;
                }}
            />
        </>
    );
};

export default UploadMedicalCertificate;